// Dynamic Module Federation host registration (MR-6464). Replaces the static
// webpack.container.ModuleFederationPlugin `remotes`/`shared` config and the
// build-time `external-remotes-plugin` URL templating with runtime `init()`,
// modeled on ai-agents/apps/agent-host/src/app/module-federation.ts.
import { init } from '@module-federation/enhanced/runtime';
import React from 'react';
import ReactDOM from 'react-dom';
import * as ReactRouterDOM from 'react-router-dom';
import * as ReactRedux from 'react-redux';
import * as StyledComponents from 'styled-components';
import * as ReactHelmetAsync from 'react-helmet-async';
import * as UiOkr from '@workboard/ui-okr';
import * as I18next from 'i18next';
import * as ReactI18next from 'react-i18next';
import * as ReactDnd from 'react-dnd';
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend';
import * as DndCore from 'dnd-core';
// Read the installed version from package.json — uniform across these shares and can't go
// stale after a bump, unlike a hardcoded string. react-dom needs this too: its PRODUCTION
// build reports a `-next-…` prerelease suffix from `.version`, which sorts below `^18.3.1`
// and so satisfies none of the consumers' shared-singleton ranges. react's own `.version`
// is a clean `18.3.1`, so this only bites react-dom — and only in production builds, which
// is why the dev server never reproduces the `Unsatisfied version` warning.
import { version as reactVersion } from 'react/package.json';
import { version as reactDomVersion } from 'react-dom/package.json';
import { version as reactRouterDomVersion } from 'react-router-dom/package.json';
import { version as reactReduxVersion } from 'react-redux/package.json';
import { version as styledComponentsVersion } from 'styled-components/package.json';
import { version as reactHelmetAsyncVersion } from 'react-helmet-async/package.json';
import { version as i18nextVersion } from 'i18next/package.json';
import { version as reactI18nextVersion } from 'react-i18next/package.json';
import { version as reactDndVersion } from 'react-dnd/package.json';
import { version as reactDndHtml5BackendVersion } from 'react-dnd-html5-backend/package.json';
import { version as dndCoreVersion } from 'dnd-core/package.json';
// Read from our own package.json — @workboard/ui-okr's exports map has no "./package.json"
// entry, so the per-package import used by the other six shares is impossible here.
// Strip any range prefix: a `^`/`~` spec is not a valid concrete version for formatShare.
import { dependencies } from '../package.json';

const uiOkrVersion = dependencies['@workboard/ui-okr'].replace(/^\D+/, '');

// Classic-layout pages (main.php) and admin/WBAdmin* never define window.vnextwobo/wbConfig
// (only vnext-main.php does) — default to {} so host construction doesn't throw on those pages.
const vnextwobo = (window.vnextwobo ?? {}) as Record<string, string>;
const wbConfig = (window.wbConfig ?? {}) as Record<string, any>;
// Cache-bust the CDN-hosted remotes exactly like the old ExternalTemplateRemotesPlugin URLs did.
const timestamp = new Date().getTime();

init({
  name: 'SERVER',
  shared: {
    react: {
      version: reactVersion,
      scope: 'default',
      lib: () => React,
      shareConfig: { singleton: true, eager: true, requiredVersion: '^18.0.0' },
    },
    'react-dom': {
      version: reactDomVersion,
      scope: 'default',
      lib: () => ReactDOM,
      shareConfig: { singleton: true, eager: true, requiredVersion: '^18.0.0' },
    },
    'react-router-dom': {
      version: reactRouterDomVersion,
      lib: () => ReactRouterDOM,
      shareConfig: { singleton: true, eager: true, requiredVersion: '^6.28.0' },
    },
    'react-redux': {
      version: reactReduxVersion,
      lib: () => ReactRedux,
      shareConfig: { singleton: true, eager: true, requiredVersion: '^7.2.4' },
    },
    'styled-components': {
      version: styledComponentsVersion,
      lib: () => StyledComponents,
      shareConfig: { singleton: true, eager: true, requiredVersion: '^5.3.6' },
    },
    'react-helmet-async': {
      version: reactHelmetAsyncVersion,
      lib: () => ReactHelmetAsync,
      // requiredVersion is optional under webpack (it infers from package.json) but is
      // REQUIRED by the enhanced runtime's SharedConfig type. '^2.0.5' is the package.json
      // range, so this preserves the old webpack behaviour rather than changing it — and
      // `eager` stays unset, exactly as the previous ModuleFederationPlugin config had it.
      shareConfig: { singleton: true, requiredVersion: '^2.0.5' },
    },
    // Single instance across host + federated remotes (kpis-app via OKR). ui-okr
    // bundles @udecode/plate-core; two copies on one page break the KPI drawer's
    // rich-text editor ("Detected multiple plate-core instances").
    '@workboard/ui-okr': {
      version: uiOkrVersion,
      lib: () => UiOkr,
      shareConfig: { singleton: true, eager: true, requiredVersion: false },
    },
    // R3 fix: host previously did not share these, so the first remote to load
    // "won" the shared scope and the loser could render raw i18n keys. The host
    // is supposed to own this context (H7, 2026-08-11) — now eager+versioned.
    i18next: {
      version: i18nextVersion,
      lib: () => I18next,
      shareConfig: { singleton: true, eager: true, requiredVersion: '^21.9.2' },
    },
    'react-i18next': {
      version: reactI18nextVersion,
      lib: () => ReactI18next,
      shareConfig: { singleton: true, eager: true, requiredVersion: '^11.18.6' },
    },
    // ui-okr >=2.20.5 no longer inlines react-dnd; remotes declare these `import: false`,
    // so the host must provide them. One copy = one HTML5 backend per page.
    'react-dnd': {
      version: reactDndVersion,
      lib: () => ReactDnd,
      shareConfig: { singleton: true, eager: true, requiredVersion: '^16.0.0' },
    },
    'react-dnd-html5-backend': {
      version: reactDndHtml5BackendVersion,
      lib: () => ReactDndHtml5Backend,
      shareConfig: { singleton: true, eager: true, requiredVersion: '^16.0.0' },
    },
    'dnd-core': {
      version: dndCoreVersion,
      lib: () => DndCore,
      shareConfig: { singleton: true, eager: true, requiredVersion: '^16.0.0' },
    },
  },
  // All producers below are classic webpack MF-1.0 `var <GLOBAL>;` bundles, so every
  // one needs the entryGlobalName/type override — not just KPI (see plans/rspack-server
  // issue 06 findings).
  remotes: [
    { name: 'SC', entry: vnextwobo.VNEXT_SCHEDULED_CONVERSATIONS_APP_REMOTE_URL, entryGlobalName: 'SC', type: 'var', shareScope: 'default' },
    { name: 'NAV', entry: vnextwobo.VNEXT_NAVIGATION_APP_REMOTE_URL, entryGlobalName: 'NAVIGATION', type: 'var', shareScope: 'default' },
    {
      name: 'OKR',
      // Prod is domain-relative per MR-8215, which deleted the KeyVault secret this used to read.
      entry: process.env.NODE_ENV === 'development'
        ? 'http://local.dt.wobo-int.com:13013/remoteEntry.js'
        : '/okrs/remoteEntry.js',
      entryGlobalName: 'OKR',
      type: 'var',
      shareScope: 'default',
    },
    { name: 'KPI', entry: vnextwobo.VNEXT_KPIS_APP_REMOTE_URL, entryGlobalName: 'KPI', type: 'var', shareScope: 'default' },
    { name: 'PERMISSIONS', entry: vnextwobo.VNEXT_PERMISSIONS_APP_REMOTE_URL, entryGlobalName: 'PERMISSIONS', type: 'var', shareScope: 'default' },
    // features-ui (vnextwobo): Admin Tools > Manage Features (COREAI-4559). Empty URL = not hosted here, filtered out below.
    { name: 'FEATURES', entry: vnextwobo.VNEXT_FEATURES_REMOTE_URL, entryGlobalName: 'FEATURES', type: 'var', shareScope: 'default' },
    { name: 'RBR', entry: `${wbConfig.rbrCdnUrl}/remoteEntry.js?t=${timestamp}`, entryGlobalName: 'RBRLandingPage', type: 'var', shareScope: 'default' },
    { name: 'SCORECARDS', entry: `${wbConfig.scorecardsCdnUrl}/remoteEntry.js?t=${timestamp}`, entryGlobalName: 'RBRScorecards', type: 'var', shareScope: 'default' },
    { name: 'SCORECARDS_GRID', entry: `${wbConfig.scorecardsGridCdnUrl}/remoteEntry.js?t=${timestamp}`, entryGlobalName: 'SCORECARDS_GRID', type: 'var', shareScope: 'default' },
    { name: 'VIRTUAL_AGENT', entry: vnextwobo.VNEXT_VIRTUAL_AGENT_APP_REMOTE_URL, entryGlobalName: 'VIRTUAL_AGENT', type: 'var', shareScope: 'default' },
    { name: 'WF', entry: vnextwobo.VNEXT_KPI_KRR_APP_REMOTE_URL, entryGlobalName: 'WF', type: 'var', shareScope: 'default' },
    // Not one of the 9 ModuleFederationPlugin remotes above — today it's loaded ad hoc by
    // RemoteAppWrapper via a manual <script> tag + `window.WorkstreamsUI` (workstreams'
    // apps/ui declares `library: { type: 'var', name: 'WorkstreamsUI' }`). Registering it
    // here is required now that RemoteAppWrapper uses loadRemote() instead of that script tag.
    { name: 'WorkstreamsUI', entry: vnextwobo.VNEXT_WORKSTREAMS_APP_REMOTE_URL, entryGlobalName: 'WorkstreamsUI', type: 'var', shareScope: 'default' },
    // A falsy entry (PERMISSIONS locally) makes registerRemote() throw on
    // `entry.startsWith(...)`, killing host construction — keep webpack's per-remote blast radius.
  ].filter((remote) => !!remote.entry),
});
